
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings } from "lucide-react";
import type { JogoBichoConfig, ConfigFormData } from "@/types/softwareTurbinado";

type ConfigFormProps = {
  config: JogoBichoConfig | null;
  onSave: (dados: ConfigFormData) => Promise<boolean>;
  isLoading: boolean;
};

const ConfigForm = ({ config, onSave, isLoading }: ConfigFormProps) => {
  const [formData, setFormData] = useState<ConfigFormData>({
    titulo_ppt: "PPT",
    titulo_ptm: "PTM",
    titulo_pt: "PT",
    titulo_ptv: "PTV",
    titulo_ptn: "PTN",
    titulo_coruja: "CORUJA",
    estado: "Rio de Janeiro",
    loteria: "Look"
  });

  useEffect(() => {
    if (config) {
      setFormData({
        titulo_ppt: config.titulo_ppt || "PPT",
        titulo_ptm: config.titulo_ptm || "PTM",
        titulo_pt: config.titulo_pt || "PT",
        titulo_ptv: config.titulo_ptv || "PTV",
        titulo_ptn: config.titulo_ptn || "PTN",
        titulo_coruja: config.titulo_coruja || "CORUJA",
        estado: config.estado || "Rio de Janeiro",
        loteria: config.loteria || "Look"
      });
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Salvando configurações:', formData);
    const success = await onSave(formData);
    if (success) {
      console.log('Configurações salvas com sucesso!');
    } else {
      console.log('Erro ao salvar configurações');
    }
  };

  const handleInputChange = (field: keyof ConfigFormData, value: string) => {
    console.log(`Alterando campo ${field} para:`, value);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personalizar Títulos dos Horários</CardTitle>
        <p className="text-sm text-muted-foreground">
          Configure os títulos personalizados para cada horário do jogo do bicho
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estado" className="text-sm font-medium">Estado da Loteria</Label>
              <Input
                id="estado"
                type="text"
                value={formData.estado}
                onChange={(e) => handleInputChange('estado', e.target.value)}
                placeholder="Ex: Rio de Janeiro"
                required
                autoComplete="off"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="loteria" className="text-sm font-medium">Nome da Loteria</Label>
              <Input
                id="loteria"
                type="text"
                value={formData.loteria}
                onChange={(e) => handleInputChange('loteria', e.target.value)}
                placeholder="Ex: Look"
                required
                autoComplete="off"
                className="mt-1"
              />
            </div>
          </div>

          {/* Títulos dos horários */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Títulos dos Horários</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="titulo_ppt" className="text-sm font-medium">1º Horário (PPT)</Label>
                <Input
                  id="titulo_ppt"
                  type="text"
                  value={formData.titulo_ppt}
                  onChange={(e) => handleInputChange('titulo_ppt', e.target.value)}
                  placeholder="Ex: PPT, 1º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="titulo_ptm" className="text-sm font-medium">2º Horário (PTM)</Label>
                <Input
                  id="titulo_ptm"
                  type="text"
                  value={formData.titulo_ptm}
                  onChange={(e) => handleInputChange('titulo_ptm', e.target.value)}
                  placeholder="Ex: PTM, 2º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="titulo_pt" className="text-sm font-medium">3º Horário (PT)</Label>
                <Input
                  id="titulo_pt"
                  type="text"
                  value={formData.titulo_pt}
                  onChange={(e) => handleInputChange('titulo_pt', e.target.value)}
                  placeholder="Ex: PT, 3º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="titulo_ptv" className="text-sm font-medium">4º Horário (PTV)</Label>
                <Input
                  id="titulo_ptv"
                  type="text"
                  value={formData.titulo_ptv}
                  onChange={(e) => handleInputChange('titulo_ptv', e.target.value)}
                  placeholder="Ex: PTV, 4º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="titulo_ptn" className="text-sm font-medium">5º Horário (PTN)</Label>
                <Input
                  id="titulo_ptn"
                  type="text"
                  value={formData.titulo_ptn}
                  onChange={(e) => handleInputChange('titulo_ptn', e.target.value)}
                  placeholder="Ex: PTN, 5º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="titulo_coruja" className="text-sm font-medium">6º Horário (CORUJA)</Label>
                <Input
                  id="titulo_coruja"
                  type="text"
                  value={formData.titulo_coruja}
                  onChange={(e) => handleInputChange('titulo_coruja', e.target.value)}
                  placeholder="Ex: CORUJA, 6º Prêmio"
                  required
                  autoComplete="off"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Botão de salvar */}
          <div className="flex justify-center pt-4">
            <Button 
              type="submit" 
              disabled={isLoading} 
              className="btn-gradient px-8 py-3 text-base font-medium"
            >
              <Settings className="h-5 w-5 mr-2" />
              {isLoading ? "Salvando Configurações..." : "Salvar Configurações"}
            </Button>
          </div>

          {/* Preview das configurações */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium mb-3">Preview das Configurações:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div><strong>Estado:</strong> {formData.estado}</div>
              <div><strong>Loteria:</strong> {formData.loteria}</div>
              <div><strong>1º:</strong> {formData.titulo_ppt}</div>
              <div><strong>2º:</strong> {formData.titulo_ptm}</div>
              <div><strong>3º:</strong> {formData.titulo_pt}</div>
              <div><strong>4º:</strong> {formData.titulo_ptv}</div>
              <div><strong>5º:</strong> {formData.titulo_ptn}</div>
              <div><strong>6º:</strong> {formData.titulo_coruja}</div>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ConfigForm;
